import { effect, Injectable, signal } from '@angular/core';
import { getDefaultPinnedMenuItemIds } from 'app/shell/components/sidebar/sidebar.utils';

export type RecentSearchType = 'page' | 'account' | 'user' | 'pair' | 'asset' | 'liquidityProvider';

export type SearchResultIcon =
  | { kind: 'material'; ligature: string }
  | { kind: 'asset'; code: string }
  | { kind: 'pair'; baseCode: string; quoteCode: string };

export interface RecentSearchEntry {
  type: RecentSearchType;
  id: string;
  label: string;
  groupPrefix?: string;
  sublabel?: string;
  icon?: SearchResultIcon;
}

const MAX_RECENT_SEARCHES = 8;

@Injectable({ providedIn: 'root' })
export class LocalSettingsService {
  public readonly compactTables = createBooleanSetting('hrn_ls_compact_tables');

  public readonly pinnedMenuItemIds = createArraySetting<string>(
    'hrn_ls_pinned_menu_items',
    getDefaultPinnedMenuItemIds(),
  );

  public readonly recentSearches = createArraySetting<RecentSearchEntry>('hrn_ls_recent_searches');

  public addRecentSearch(entry: RecentSearchEntry) {
    this.recentSearches.update(entries => {
      const withoutEntry = entries.filter(existing => existing.type !== entry.type || existing.id !== entry.id);
      return [entry, ...withoutEntry].slice(0, MAX_RECENT_SEARCHES);
    });
  }
}

function createBooleanSetting(settingName: string) {
  const value = signal(localStorage.getItem(settingName) === 'true');

  effect(() => {
    if (value()) {
      localStorage.setItem(settingName, 'true');
    } else {
      localStorage.removeItem(settingName);
    }
  });

  return value;
}

function createArraySetting<T>(settingName: string, initial: T[] = []) {
  const value = signal<T[]>(readArraySetting(settingName, initial));

  effect(() => {
    localStorage.setItem(settingName, JSON.stringify(value()));
  });

  return value;
}

function readArraySetting<T>(settingName: string, initial: T[]): T[] {
  const raw = localStorage.getItem(settingName);
  if (!raw) {
    return initial;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return initial;
  }
}
